import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@auctisync/ui';
import { Link } from 'react-router-dom';
import { SignOutButton } from '../../components/SignOutButton';
import { readClaims, useSession } from '../../hooks/useSession';

/**
 * Post-auth landing for AuctiSync super-admins. Day-4 stub.
 */
export function SuperDashboard() {
  const { session } = useSession();
  const { appRole } = readClaims(session);

  return (
    <main style={{ padding: '2rem', maxWidth: '64rem', margin: '0 auto' }}>
      <div
        style={{
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '1rem',
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: 'var(--font-heading-active)',
              fontSize: '1.875rem',
              fontWeight: 700,
              color: 'var(--text)',
              marginBottom: '0.25rem',
            }}
          >
            Super admin console
          </h1>
          <p style={{ color: 'var(--text-muted)' }}>
            Signed in as {session?.user.email ?? 'unknown'} ·{' '}
            <Badge variant="outline">{appRole ?? 'no role'}</Badge>
          </p>
        </div>
        <SignOutButton redirectTo="/super" />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(20rem, 1fr))',
          gap: '1rem',
        }}
      >
        <Card>
          <CardHeader>
            <CardTitle>Intelligence Engine</CardTitle>
            <CardDescription>Operational telemetry + signals.</CardDescription>
          </CardHeader>
          <CardContent>
            <ul
              style={{
                margin: 0,
                paddingLeft: '1.1rem',
                color: 'var(--text)',
                fontSize: '0.9rem',
                lineHeight: 1.7,
              }}
            >
              <li>
                <Link
                  to="/super/intelligence/engine-telemetry"
                  style={{ color: 'var(--brand-primary)', fontWeight: 600 }}
                >
                  Engine telemetry
                </Link>{' '}
                — latency, throughput, error rate, gauges
              </li>
              <li>
                <Link
                  to="/super/intelligence/errors"
                  style={{ color: 'var(--brand-primary)', fontWeight: 600 }}
                >
                  Error clusters
                </Link>{' '}
                — Sentry-grouped issues clustered across the 3 projects
              </li>
              <li>
                <Link
                  to="/super/intelligence/sales"
                  style={{ color: 'var(--brand-primary)', fontWeight: 600 }}
                >
                  Sales analytics
                </Link>{' '}
                — cross-tenant sell-through, pricing, top categories, engagement
              </li>
              <li>
                <Link
                  to="/super/intelligence/competitors"
                  style={{ color: 'var(--brand-primary)', fontWeight: 600 }}
                >
                  Competitor research
                </Link>{' '}
                — daily scrape of auction-software competitors, pricing-signal-flagged diffs
              </li>
              <li>
                <Link
                  to="/super/intelligence/predictions"
                  style={{ color: 'var(--brand-primary)', fontWeight: 600 }}
                >
                  Pricing predictions
                </Link>{' '}
                — cross-tenant per-lot hammer + sell-through predictions, full provenance
              </li>
              <li>
                <Link
                  to="/super/intelligence/churn"
                  style={{ color: 'var(--brand-primary)', fontWeight: 600 }}
                >
                  Tenant churn signals
                </Link>{' '}
                — per-tenant 30-day vs prior-30 activity drops, severity-bucketed
              </li>
              <li>
                <Link
                  to="/super/intelligence/briefs"
                  style={{ color: 'var(--brand-primary)', fontWeight: 600 }}
                >
                  Briefs
                </Link>{' '}
                — latest daily diagnostics + Monday weekly synthesis (Telegram + dashboard)
              </li>
              <li>
                <Link
                  to="/super/copilot-gaps"
                  style={{ color: 'var(--brand-primary)', fontWeight: 600 }}
                >
                  Copilot capability gaps
                </Link>{' '}
                — backlog of asks the copilot couldn&apos;t fulfill, clustered by tool to build next
              </li>
              <li>
                <Link
                  to="/super/help-feedback"
                  style={{ color: 'var(--brand-primary)', fontWeight: 600 }}
                >
                  Help feedback
                </Link>{' '}
                — which help articles readers said did not help them, worst first
              </li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>App health</CardTitle>
            <CardDescription>What the apps report from the field.</CardDescription>
          </CardHeader>
          <CardContent>
            <ul
              style={{
                margin: 0,
                paddingLeft: '1.1rem',
                color: 'var(--text)',
                fontSize: '0.9rem',
                lineHeight: 1.7,
              }}
            >
              <li>
                <Link
                  to="/super/app-reports"
                  style={{ color: 'var(--brand-primary)', fontWeight: 600 }}
                >
                  App reports
                </Link>{' '}
                — Cataloging app freezes, errors and “Report a problem”, with what was on screen
              </li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Tenant operations</CardTitle>
            <CardDescription>Onboarding + lifecycle tools.</CardDescription>
          </CardHeader>
          <CardContent>
            <ul
              style={{
                margin: 0,
                paddingLeft: '1.1rem',
                color: 'var(--text)',
                fontSize: '0.9rem',
                lineHeight: 1.7,
              }}
            >
              <li>
                <Link
                  to="/super/onboarding-reset"
                  style={{ color: 'var(--brand-primary)', fontWeight: 600 }}
                >
                  Reset tenant onboarding
                </Link>{' '}
                — clear a tenant&apos;s progress + staged import and re-draft (DTA dogfood)
              </li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Day 4 scaffold</CardTitle>
            <CardDescription>
              Super-admin surface theme + banner + claim-gated route primitive are live.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
              Real console (impersonation, audit log viewer) lands in a later module.
            </p>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
